import React from 'react';
import { Box, Typography, TextField, Select, MenuItem, FormControl, InputLabel, Stack } from '@mui/material';
import { iconOptions } from '../../../business/NitzavSpotlightWidget/iconOptions';

const navy = '#1e3a5f';

const NITZAV_TYPES = [
  { value: 'נציב היום',          label: 'נציב היום' },
  { value: 'לעילוי נשמת',        label: 'לעילוי נשמת' },
  { value: 'לכבוד',              label: 'לכבוד' },
  { value: 'לרפואת',             label: 'לרפואת' },
  { value: 'לזכות',              label: 'לזכות' },
  { value: 'לכבוד נישואי',       label: 'לכבוד נישואי' },
  { value: 'לכבוד בר המצווה',    label: 'לכבוד בר המצווה' },
  { value: 'לכבוד בת המצווה',    label: 'לכבוד בת המצווה' },
  { value: 'אחר',                label: 'אחר' },
];

const inputSx = { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' } };

/**
 * NitzavStep
 * פרטי הנציב — שם, סוג נציבות, הקדשה, אייקון
 *
 * Props:
 *   formData  { nitzavData: { name, type, dedication, iconId } }
 *   onChange  (partial) => void
 */
const NitzavStep = ({ formData, onChange }) => {
  const data = formData?.nitzavData || {};
  const name       = data.name       || '';
  const type       = data.type       || 'נציב היום';
  const dedication = data.dedication || '';
  const iconId     = data.iconId     || 'star';

  const update = (partial) => onChange({ nitzavData: { ...data, ...partial } });

  return (
    <Box dir="rtl">
      <Typography sx={{ fontWeight: 700, color: navy, fontSize: '1rem', mb: 0.5 }}>
        פרטי הנציב
      </Typography>
      <Typography sx={{ fontSize: '0.8rem', color: '#6b7280', mb: 2.5, lineHeight: 1.5 }}>
        הזן את שם הנציב, סוג הנציבות וטקסט ההקדשה.
      </Typography>

      <Box sx={{ bgcolor: 'white', border: '1px solid #e5e7eb', borderRadius: 2, p: 2.5, mb: 2 }}>
        <Stack spacing={2}>
          {/* Name */}
          <TextField
            size="small"
            label="שם הנציב"
            placeholder="ישראל ישראלי ז׳ל"
            value={name}
            onChange={(e) => update({ name: e.target.value })}
            fullWidth
            sx={inputSx}
          />

          {/* Type */}
          <FormControl size="small" fullWidth>
            <InputLabel sx={{ fontSize: '0.82rem' }}>סוג נציבות</InputLabel>
            <Select
              label="סוג נציבות"
              value={type}
              onChange={(e) => update({ type: e.target.value })}
              sx={{ ...inputSx, fontSize: '0.82rem' }}
            >
              {NITZAV_TYPES.map(t => (
                <MenuItem key={t.value} value={t.value} sx={{ fontSize: '0.82rem' }}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Dedication */}
          <TextField
            size="small"
            label="הקדשה (אופציונלי)"
            placeholder="לזכר נשמתו הטהורה..."
            value={dedication}
            onChange={(e) => update({ dedication: e.target.value })}
            multiline
            minRows={2}
            fullWidth
            sx={inputSx}
          />
        </Stack>
      </Box>

      {/* Icon */}
      <Box sx={{ bgcolor: 'white', border: '1px solid #e5e7eb', borderRadius: 2, p: 2 }}>
        <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.875rem', mb: 1.5 }}>
          אייקון
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {iconOptions.map(({ id, label, icon: IconComponent }) => {
            const isSelected = iconId === id;
            return (
              <Box
                key={id}
                onClick={() => update({ iconId: id })}
                sx={{
                  width: 54,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '3px',
                  py: 0.75,
                  px: 0.5,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  border: isSelected ? '2px solid #111827' : '1px solid #e5e7eb',
                  bgcolor: isSelected ? '#f9fafb' : 'white',
                  boxShadow: isSelected ? '0 0 0 3px rgba(17,24,39,0.06)' : 'none',
                  '&:hover': {
                    borderColor: isSelected ? '#111827' : '#9ca3af',
                    bgcolor: isSelected ? '#f9fafb' : '#f8fafc',
                  },
                }}
              >
                <IconComponent sx={{ fontSize: 20, color: isSelected ? '#111827' : '#6b7280' }} />
                <Typography sx={{
                  fontSize: '0.6rem',
                  color: isSelected ? '#111827' : '#9ca3af',
                  fontWeight: isSelected ? 600 : 400,
                  lineHeight: 1,
                }}>
                  {label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default NitzavStep;
